import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { IoFlashOutline, IoLocationOutline, IoAlertCircleOutline, IoChatbubbleEllipsesOutline, IoHeartOutline } from "react-icons/io5";
import { useLanguage } from "./contexts/LanguageContext";
import LanguageSelector from "./components/LanguageSelector";
import LocationButton from "./components/LocationButton";
import SearchBox from "./components/SearchBox";
import { formatDistance } from "./utils/geolocation";
import AppLandingSection from "./components/AppLandingSection";
import EmptyStateAppPromo from "./components/EmptyStateAppPromo";
import "./LocationLandingPage.css";

function LocationLandingPage() {
  const [nearbyStations, setNearbyStations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchLocation, setSearchLocation] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();

  // Restore search location state when navigating back (but not on page refresh)
  useEffect(() => {
    // Check if this is a fresh page load by looking at sessionStorage
    const isPageRefresh = !sessionStorage.getItem('app_navigation');
    
    if (isPageRefresh) {
      // This is a page refresh - clear any cached state and start fresh
      setNearbyStations([]);
      setSearchLocation(null);
      setError(null);
      setLoading(false);
    } else if (location.state?.searchLocation && location.state?.nearbyStations) {
      // This is navigation - restore the previous state
      setSearchLocation(location.state.searchLocation);
      setNearbyStations(location.state.nearbyStations);
    }
    
    // Mark that navigation has happened
    sessionStorage.setItem('app_navigation', 'true');
    setIsInitialized(true);
  }, [location.state]);


  const handleLocationFound = async (lat, lon, accuracy) => {
    setLoading(true);
    setError(null);
    
    try {
      
      // Use our new nearby stations API
      const response = await fetch(
        `/api/stations/nearby?lat=${lat}&lon=${lon}&limit=10&radius=25`
      );
      
      if (!response.ok) {
        throw new Error(`Failed to fetch nearby stations: ${response.status}`);
      }
      
      const stations = await response.json();
      
      setNearbyStations(stations);
      setSearchLocation({
        lat,
        lon,
        type: 'gps',
        description: t('nearYourLocation')
      });
      
    } catch (error) {
      console.error('Error fetching nearby stations:', error);
      setError(t('errorLoadingStations'));
    } finally {
      setLoading(false);
    }
  };

  const handleLocationError = (errorMessage) => {
    console.error('Location error:', errorMessage);
    setError(errorMessage);
    setLoading(false);
  };

  const handleSearch = async (query) => {
    setLoading(true);
    setError(null);
    
    try {
      
      // Determine if query is zipcode or city
      const isZipcode = /^\d{5}(-\d{4})?$/.test(query.trim());
      
      let response;
      if (isZipcode) {
        // Search by zipcode
        response = await fetch(
          `/api/stations/search?zipcode=${encodeURIComponent(query)}&radius=15&limit=10`
        );
      } else {
        // Search by city
        response = await fetch(
          `/api/stations/search?city=${encodeURIComponent(query)}`
        );
      }
      
      if (!response.ok) {
        throw new Error(`Search failed: ${response.status}`);
      }
      
      const stations = await response.json();
      
      setNearbyStations(stations);
      setSearchLocation({
        query,
        type: isZipcode ? 'zipcode' : 'city',
        description: `Near ${query}`
      });
      
    } catch (error) {
      console.error('Search error:', error);
      setError(t('errorLoadingStations'));
    } finally {
      setLoading(false);
    }
  };

  const handleStationClick = (station) => {
    navigate(`/station/${station.id}`, { 
      state: { 
        station,
        searchLocation, // Pass search context (location, city, zipcode, or county)
        nearbyStations // Pass stations list for restoration
      } 
    });
  };


  const handleFeedbackClick = () => {
    navigate('/feedback');
  };

  const clearResults = () => {
    setNearbyStations([]);
    setSearchLocation(null);
    setError(null);
    setLoading(false);
    // Clear the navigation flag so next refresh starts fresh
    sessionStorage.removeItem('app_navigation');
  };

  return (
    <div className="location-landing-container">
      {/* Header */}
      <div className="location-header">
        <LanguageSelector />
        <div className="location-header-content">
          <div className="location-header-text">
            <h1>{t('title')}</h1>
            <p>{t('subtitle')}</p>
          </div>
        </div>
      </div>


      {/* Main Content */}
      <div className="location-content">
        {!nearbyStations.length && !loading ? (
          // Landing state - show search options
          <div className="location-search-section">
            <div className="location-search-card">
              <div className="location-search-header">
                <div className="location-search-header-title">
                  <IoFlashOutline size={32} color="var(--color-primary)" />
                  <h2>{t('findNearestStations')}</h2>
                </div>
                <div className="location-coverage-info">
                  <div className="location-coverage-line">
                    <IoLocationOutline size={16} />
                    <span className="location-coverage-current">{t('coverageStatus')}</span>
                  </div>
                  <div className="location-coverage-line">
                    <span className="location-coverage-separator">•</span>
                    <span className="location-coverage-expansion">{t('coverageDetails')}</span>
                  </div>
                </div>
              </div>
              
              {/* Primary CTA - Location Button */}
              <div className="location-primary-cta">
                <LocationButton
                  onLocationFound={handleLocationFound}
                  onLocationError={handleLocationError}
                  variant="primary"
                />
              </div>
              
              {/* Divider */}
              <div className="location-divider">
                <span>{t('or')}</span>
              </div>
              
              {/* Search Box */}
              <div className="location-search-box">
                <SearchBox
                  onSearch={handleSearch}
                  placeholder={t('searchPlaceholder')}
                />
              </div>
            </div>

            <AppLandingSection />

            {/* Error display */}
            {error && (
              <div className="location-error">
                <IoAlertCircleOutline size={24} />
                <p>{error}</p>
              </div>
            )}
          </div>
        ) : (
          // Results state - show nearby stations
          <div className="location-results-section">
            <div className="location-results-header">
              <div className="location-results-info">
                <h2>
                  {searchLocation?.description || t('nearbyStations')}
                </h2>
                {nearbyStations.length > 0 && (
                  <p>{nearbyStations.length} {nearbyStations.length === 1 ? t('station') : t('stations')} found</p>
                )}
              </div>
              <button 
                onClick={clearResults}
                className="location-new-search-button"
              >
                {t('newSearch')}
              </button>
            </div>
            
            {loading ? (
              <div className="location-loading">
                <IoFlashOutline size={32} />
                <p>{t('loadingStations')}</p>
              </div>
            ) : error ? (
              <div className="location-error">
                <IoAlertCircleOutline size={24} />
                <p>{error}</p>
              </div>
            ) : nearbyStations.length === 0 ? (
              <div className="location-no-results">
                <IoLocationOutline size={48} />
                <h3>{t('noStationsFound')}</h3>
                <p>{t('tryDifferentLocation')}</p>
                <EmptyStateAppPromo />
              </div>
            ) : (
              <div className="location-station-list">
                {nearbyStations.map((station) => (
                  <div
                    key={station.id}
                    className={`location-station-card ${
                      station.availablePorts > 0 ? 'has-available' : ''
                    }`}
                    onClick={() => handleStationClick(station)}
                  >
                    <div className="location-station-main">
                      <div className="location-station-info">
                        <h3>{station.name}</h3>
                        <div className="location-station-address-row">
                          <p className="location-station-address">
                            <IoLocationOutline size={14} />
                            {station.address}
                          </p>
                          {station.distance && (
                            <span className="location-station-distance">
                              {formatDistance(station.distance)}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="location-station-status">
                        <div className="location-status-badges">
                          {/* Availability Badge - Left */}
                          <span className={`location-availability-badge ${
                            station.availablePorts > 0 ? 'available' : 'unavailable'
                          }`}>
                            {station.availablePorts}/{station.totalPorts} {t('available')}
                          </span>
                          
                          {/* Busyness Status Badge - Right */}
                          {station.busynessStatus && (
                            <span className={`location-busyness-badge busyness-${station.busynessStatus}`}>
                              {station.busynessStatus === 'no-wait' && t('noWait')}
                              {station.busynessStatus === 'short-wait' && t('shortWait')}
                              {station.busynessStatus === 'busy' && t('busy')}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <AppLandingSection />
          </div>
        )}
      </div>
      
      {/* Bottom Actions - Feedback and Sponsor */}
      <div className="location-bottom-actions">
        <button className="feedback-button" onClick={handleFeedbackClick}>
          <IoChatbubbleEllipsesOutline size={20} />
          {t('feedbackButton')}
        </button>
        
        <a
          href="https://ko-fi.com/leefreeman"
          target="_blank"
          rel="noopener noreferrer"
          className="sponsor-button"
        >
          <IoHeartOutline size={14} />
          {t('buyMeACoffee')}
        </a>
      </div>
    </div>
  );
}

export default LocationLandingPage;